import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import EncryptionKeyModal from './components/EncryptionKeyModal';
import './i18n';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ProfilePage from './pages/ProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import VerificationPendingPage from './pages/VerificationPendingPage';
import ClientListPage from './pages/ClientListPage';
import ClientCreatePage from './pages/ClientCreatePage';
import CompanyListPage from './pages/CompanyListPage';
import CompanyCreatePage from './pages/CompanyCreatePage';
import InvoiceListPage from './pages/InvoiceListPage';
import InvoiceCreatePage from './pages/InvoiceCreatePage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import TemplateManagementPage from './pages/TemplateManagementPage';
import AgentManagementPage from './pages/AgentManagementPage';
import CategoriesPage from './pages/CategoriesPage';
import ProductsPage from './pages/ProductsPage';
import OrdersPage from './pages/OrdersPage';
import StockMovementsPage from './pages/StockMovementsPage';
import SuppliersPage from './pages/SuppliersPage';
import EntityConfigsPage from './pages/admin/EntityConfigsPage';
import EntitySubscribersPage from './pages/admin/EntitySubscribersPage';
import AppConfigPage from './pages/admin/AppConfigPage';
import PublicInvoiceViewer from './pages/PublicInvoiceViewer';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';

// Authenticated home redirect component
const AuthenticatedHomeRedirect: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 spinner mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // Check if user is verified (only for non-admin users)
    if (!user.isAdmin && !user.isVerified) {
      return <Navigate to="/verification-pending" state={{ email: user.email, message: 'Please verify your email to access your account.' }} replace />;
    }
    return <Navigate to={user.isAdmin ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return <Navigate to="/login" replace />;
};

// Main app component with routing
const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/app" element={<AuthenticatedHomeRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/verification-pending" element={<VerificationPendingPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/entity-configs"
          element={
            <ProtectedRoute requireAdmin={true}>
              <EntityConfigsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/entity-configs/:entityConfigId/subscribers"
          element={
            <ProtectedRoute requireAdmin={true}>
              <EntitySubscribersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/app-config"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AppConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <ClientListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/create"
          element={
            <ProtectedRoute>
              <ClientCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <ProtectedRoute>
              <CompanyListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies/create"
          element={
            <ProtectedRoute>
              <CompanyCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies/:companyId/edit"
          element={
            <ProtectedRoute>
              <CompanyCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <InvoiceListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices/create"
          element={
            <ProtectedRoute>
              <InvoiceCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices/:invoiceId/edit"
          element={
            <ProtectedRoute>
              <InvoiceEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <TemplateManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents"
          element={
            <ProtectedRoute>
              <AgentManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute>
              <CategoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-movements"
          element={
            <ProtectedRoute>
              <StockMovementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute>
              <SuppliersPage />
            </ProtectedRoute>
          }
        />
        <Route path="/preview/:generationId" element={<PublicInvoiceViewer />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Root app component with authentication provider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <CompanyProvider>
        <EncryptionKeyModal />
        <AppRoutes />
      </CompanyProvider>
    </AuthProvider>
  );
};

export default App;
